/**
 * Lead detail cards: evidence path, Forsyth tax, and present loan value.
 */
import { formatInteger } from "./format.js";
import {
  detailMetric,
  escapeAttr,
  escapeHtml,
  labelize,
  pill
} from "./dashboard-html.js";
import type {
  LeadFactoryFact,
  LeadFactoryLeadDetailResponse,
  LeadFactorySourceDocument
} from "./dashboard-lead-factory-types.js";

export function leadDetailEvidencePathHtml(detail: LeadFactoryLeadDetailResponse): string {
  const lead = detail.lead;
  const primaryDocument = detail.documents[0];
  const openTasks = detail.tasks.filter((task) => ["open", "assigned", "blocked"].includes(task.status));
  const propertyKnown = Boolean(lead.property_address);
  const ownerKnown = Boolean(lead.owner_name || detail.owners.length > 0);
  const mortgageKnown = typeof lead.mortgage_amount === "number" && lead.mortgage_amount > 0;
  const missingFacts = [
    propertyKnown ? "" : "property",
    ownerKnown ? "" : "owner",
    mortgageKnown ? "" : "mortgage"
  ].filter(Boolean);
  const sourceCoordinate = primaryDocument
    ? [
        primaryDocument.source_document_type || "unknown",
        primaryDocument.book && primaryDocument.page ? `book ${primaryDocument.book} page ${primaryDocument.page}` : "",
        primaryDocument.recording_date || primaryDocument.document_date || ""
      ].filter(Boolean).join(" · ")
    : "No linked source document";
  return `
    <section class="lead-detail-evidence-path" data-lead-detail-evidence-path>
      <div class="detail-context-actions">
        <a class="button secondary" href="#lead-production-command-center">Command Center</a>
        <a class="button secondary" href="#ready-leads">Ready Leads</a>
        <a class="button secondary" href="#lead-blockers">Blockers</a>
      </div>
      <div class="lead-detail-evidence-grid">
        <article data-lead-detail-fact-readiness>
          <span>Fact readiness</span>
          <strong>${missingFacts.length === 0 ? "Core facts present" : `${formatInteger(missingFacts.length)} missing`}</strong>
          <small>${missingFacts.length === 0 ? "property, owner, and mortgage are populated" : missingFacts.map(labelize).join(", ")}</small>
        </article>
        <article data-lead-detail-source-provenance>
          <span>Source provenance</span>
          <strong>${primaryDocument ? escapeHtml(primaryDocument.id) : "No source"}</strong>
          <small>${escapeHtml(sourceCoordinate)}</small>
          ${
            primaryDocument
              ? `<div class="inline-actions">
                  <button class="button secondary" type="button" data-source-document-review="${escapeAttr(primaryDocument.id)}">Review PDF</button>
                  <button class="button secondary" type="button" data-source-document-preview="${escapeAttr(primaryDocument.id)}">Open PDF</button>
                </div>`
              : ""
          }
        </article>
        <article data-lead-detail-contact-readiness>
          <span>Contact readiness</span>
          <strong>${escapeHtml(labelize(lead.contact_readiness))}</strong>
          <small>${formatInteger(detail.contacts.length)} contacts · ${formatInteger(openTasks.length)} open tasks</small>
        </article>
        <article data-lead-detail-audit-trail>
          <span>Audit trail</span>
          <strong>${formatInteger(detail.facts.length)} facts</strong>
          <small>${formatInteger(detail.events.length)} workflow events</small>
        </article>
      </div>
    </section>
  `;
}

export function leadDetailNoticeHtml(detail: LeadFactoryLeadDetailResponse): string {
  const facts = detail.facts ?? [];
  const noticeDocument = (detail.documents ?? []).find((document) => document.source_document_type === "notice_of_foreclosure");
  const present = Boolean(noticeDocument);
  const amount = present
    ? leadFactValue(facts, "mortgage_amount") || (detail.lead.mortgage_amount != null ? String(detail.lead.mortgage_amount) : "")
    : "";
  const loanDate = present ? leadFactValue(facts, "original_loan_date") : "";
  const amountLabel = amount
    ? (Number.isFinite(Number(amount)) ? `$${Number(amount).toLocaleString("en-US")}` : amount)
    : "";
  return `
    <section class="lead-detail-notice" data-lead-detail-notice data-notice-status="${escapeAttr(present ? "present" : "missing")}">
      <article>
        <span>eCourts notice</span>
        <strong>${escapeHtml(present ? "Present" : "Missing")}</strong>
        <small>${escapeHtml(
          present
            ? [noticeDocument?.id, amountLabel, loanDate ? `date ${loanDate}` : ""].filter(Boolean).join(" · ")
            : "No foreclosure notice document on this lead."
        )}</small>
      </article>
    </section>
  `;
}

const ORIGINAL_DEED_RELATIONSHIPS = new Set([
  "referenced_original_deed",
  "referenced_original_deed_of_trust",
  "reused_referenced_original_deed_of_trust"
]);

export function leadDetailOriginalDeedHtml(detail: LeadFactoryLeadDetailResponse): string {
  const facts = detail.facts ?? [];
  const deedDocument = (detail.documents ?? []).find((document) => ORIGINAL_DEED_RELATIONSHIPS.has(String(document.relationship ?? "")));
  const book = leadFactValue(facts, "referenced_deed_of_trust_book");
  const page = leadFactValue(facts, "referenced_deed_of_trust_page");
  const bookPage = leadFactValue(facts, "referenced_deed_of_trust_book_page") || (book && page ? `${book}/${page}` : "");
  const present = Boolean(deedDocument);
  const coordinates = bookPage || (deedDocument?.book && deedDocument?.page ? `${deedDocument.book}/${deedDocument.page}` : "");
  const status = present ? "present" : coordinates ? "coordinates_only" : "missing";
  const heading = present ? "Present" : coordinates ? "Book/page only" : "Missing";
  const detailLine = present
    ? [deedDocument?.id, coordinates ? `book/page ${coordinates}` : ""].filter(Boolean).join(" · ")
    : coordinates
      ? `Book/page ${coordinates} is on the lead; original deed not fetched.`
      : "No original deed or book/page on this lead.";
  return `
    <section class="lead-detail-original-deed" data-lead-detail-original-deed data-deed-status="${escapeAttr(status)}">
      <article>
        <span>Original deed</span>
        <strong>${escapeHtml(heading)}</strong>
        <small>${escapeHtml(detailLine)}</small>
      </article>
    </section>
  `;
}

export function leadDetailForsythTaxHtml(detail: LeadFactoryLeadDetailResponse): string {
  const facts = detail.facts ?? [];
  const owner = leadFactValue(facts, "owner_name") || detail.lead.owner_name || "";
  const mailing = leadFactValue(facts, "mailing_address");
  const assessed = leadFactValue(facts, "assessed_value");
  const parcelId = leadFactValue(facts, "parcel_id") || detail.lead.parcel_id || "";
  const taxUrl = leadFactValue(facts, "tax_record_url");
  const hasTax = Boolean(mailing || assessed || taxUrl);
  const assessedLabel = assessed
    ? `$${Number(assessed).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "Missing";
  return `
    <section class="lead-detail-forsyth-tax" data-lead-detail-forsyth-tax data-tax-status="${escapeAttr(hasTax ? "present" : "missing")}">
      <article>
        <span>Forsyth tax</span>
        <strong>${escapeHtml(hasTax ? owner || "Owner on tax record" : "Not applied")}</strong>
        <small>${escapeHtml(
          hasTax
            ? [mailing ? `mailing ${mailing}` : "", `assessed ${assessedLabel}`, parcelId ? `parcel ${parcelId}` : ""].filter(Boolean).join(" · ")
            : "No Forsyth tax owner, mailing, or assessed value on this lead."
        )}</small>
        ${taxUrl ? `<small>${escapeHtml(taxUrl)}</small>` : ""}
      </article>
    </section>
  `;
}

export function leadDetailCurrentBalanceHtml(detail: LeadFactoryLeadDetailResponse): string {
  const amount = leadFactValue(detail.facts ?? [], "current_balance");
  const present = Boolean(amount);
  return `
    <section class="lead-detail-current-balance" data-lead-detail-current-balance data-current-balance-status="${escapeAttr(present ? "present" : "missing")}">
      <article>
        <span>Current balance</span>
        <strong>${escapeHtml(present ? formatLeadMoney(amount) : "Missing")}</strong>
        <small>${escapeHtml(present ? "From the foreclosure notice payoff language." : "No current/payoff balance on this lead.")}</small>
      </article>
    </section>
  `;
}

export function leadDetailCurrentLoanValueHtml(detail: LeadFactoryLeadDetailResponse): string {
  const facts = detail.facts ?? [];
  const plvStatus = leadFactValue(facts, "present_loan_value_status");
  const plv = leadFactValue(facts, "present_loan_value");
  const noticeValue = leadFactValue(facts, "current_loan_value") || leadFactValue(facts, "current_balance");
  const usePlv = plvStatus === "estimated" && Boolean(plv);
  const amount = usePlv ? plv : noticeValue;
  const present = Boolean(amount);
  const source = usePlv ? "present loan value" : present ? "notice current balance" : "";
  return `
    <section class="lead-detail-current-loan-value" data-lead-detail-current-loan-value data-current-loan-value-status="${escapeAttr(present ? "present" : "missing")}" data-current-loan-value-source="${escapeAttr(source || "none")}">
      <article>
        <span>Current loan value</span>
        <strong>${escapeHtml(present ? formatLeadMoney(amount) : "Missing")}</strong>
        <small>${escapeHtml(present ? `From ${source}.` : "No present loan value or notice current balance on this lead.")}</small>
      </article>
    </section>
  `;
}

export function leadDetailPresentLoanHtml(detail: LeadFactoryLeadDetailResponse): string {
  const facts = detail.facts ?? [];
  const status = leadFactValue(facts, "present_loan_value_status");
  const value = leadFactValue(facts, "present_loan_value");
  const sourcePage = leadFactValue(facts, "present_loan_value_source_page");
  const originalPrincipal = leadFactValue(facts, "present_loan_value_original_principal");
  const originalLoanDate = leadFactValue(facts, "present_loan_value_original_loan_date");
  const missingReason = leadFactValue(facts, "present_loan_value_missing_reason");
  const heading = status === "estimated" && value
    ? `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : status === "missing"
      ? "Missing"
      : "Not calculated";
  const sourceLabel = sourcePage && sourcePage !== "missing" ? `source page ${sourcePage}` : "source page missing";
  const detailLine = status === "estimated"
    ? `${sourceLabel}${originalPrincipal && originalLoanDate ? ` · original $${Number(originalPrincipal).toLocaleString("en-US")} on ${originalLoanDate}` : ""}`
    : missingReason || "No source-backed original amount and date on this lead.";
  return `
    <section class="lead-detail-present-loan" data-lead-detail-present-loan data-plv-status="${escapeAttr(status || "not_calculated")}">
      <article>
        <span>Present loan value</span>
        <strong>${escapeHtml(heading)}</strong>
        <small>${escapeHtml(detailLine)}</small>
      </article>
    </section>
  `;
}

function formatLeadMoney(amount: string): string {
  const numeric = Number(String(amount).replace(/[$,]/g, ""));
  if (Number.isFinite(numeric)) {
    return `$${numeric.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return amount;
}

export function leadFactValue(facts: LeadFactoryFact[], fieldName: string): string {
  const fact = facts.find((row) => row.field_name === fieldName && row.review_status !== "rejected");
  return String(fact?.normalized_value || fact?.value || "").trim();
}

export function leadDetailSection(title: string, items: string[], empty: string): string {
  return `
    <section>
      <h3>${escapeHtml(title)}</h3>
      <div class="detail-list">
        ${items.join("") || `<p class="empty">${escapeHtml(empty)}</p>`}
      </div>
    </section>
  `;
}

export function sourceDocumentDetailHtml(document: LeadFactorySourceDocument): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(document.id)}</strong>
        <small>${escapeHtml(document.source_document_type || "unknown")} · ${escapeHtml(document.classification_status || "unclassified")} · ${escapeHtml(document.extraction_status || "not started")}</small>
        <p>${escapeHtml(document.artifact_uri || "No artifact URI")}</p>
      </div>
      <div class="inline-actions">
        <button class="button secondary" type="button" data-source-document-review="${escapeAttr(document.id)}">Review</button>
        <button class="button secondary" type="button" data-source-document-classify="${escapeAttr(document.id)}" data-source-document-class="${escapeAttr(document.source_document_type || "unknown")}">Classify</button>
        <button class="button secondary" type="button" data-source-document-preview="${escapeAttr(document.id)}">Open PDF</button>
        ${pill(labelize(document.relationship || "source"), document.relationship || "source")}
      </div>
    </article>
  `;
}

